import * as THREE from 'three'
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { isMobile } from '../core/viewport.js'

let loadedFont = null

export const loadFont = async () => {
    const loader = new FontLoader()
    loadedFont = await loader.loadAsync('https://threejs.org/examples/fonts/helvetiker_bold.typeface.json')
    return loadedFont
}

export const createTextGeometry = (text, size = 2) => {
    const font = loadedFont
    if (isMobile && text.length > 2) {
        const letters = text.split('')
        const letterSize = size * 0.8
        const letterSpacing = size * 1.0
        const totalHeight = letters.length * letterSpacing
        const geometries = []
        letters.forEach((letter, i) => {
            const geo = new TextGeometry(letter, {
                font, size: letterSize, depth: size * 0.15,
                curveSegments: 12, bevelEnabled: true,
                bevelThickness: 0.04, bevelSize: 0.04, bevelSegments: 3
            })
            geo.center()
            geo.translate(0, (totalHeight / 2) - i * letterSpacing - letterSpacing / 2, 0)
            geometries.push(geo)
        })
        const merged = mergeGeometries(geometries)
        merged.center()
        return merged
    }
    const geo = new TextGeometry(text, {
        font, size, depth: size * 0.2,
        curveSegments: 12, bevelEnabled: true,
        bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 3
    })
    geo.center()
    return geo
}

export const createShapeGeometry = (shape, params) => {
    const s = params.shapeScale
    switch (shape) {
        case 'sphere':
            return new THREE.SphereGeometry(2.5 * s, 64, 64)
        case 'torusknot':
            return new THREE.TorusKnotGeometry(2.0 * s, 0.6 * s, 200, 32)
        case 'torus':
            return new THREE.TorusGeometry(2.5 * s, 0.8 * s, 32, 100)
        case 'heart': {
            const heartShape = new THREE.Shape()
            const x = 0, y = 0
            heartShape.moveTo(x, y + 1.5)
            heartShape.bezierCurveTo(x, y + 2.4, x - 1.2, y + 3.3, x - 2.5, y + 3.3)
            heartShape.bezierCurveTo(x - 4.5, y + 3.3, x - 4.5, y + 1.2, x - 4.5, y + 1.2)
            heartShape.bezierCurveTo(x - 4.5, y, x - 3.5, y - 1.5, x, y - 3.6)
            heartShape.bezierCurveTo(x + 3.5, y - 1.5, x + 4.5, y, x + 4.5, y + 1.2)
            heartShape.bezierCurveTo(x + 4.5, y + 1.2, x + 4.5, y + 3.3, x + 2.5, y + 3.3)
            heartShape.bezierCurveTo(x + 1.2, y + 3.3, x, y + 2.4, x, y + 1.5)
            const extrudeSettings = { depth: 0.8 * s, bevelEnabled: true, bevelSize: 0.2, bevelThickness: 0.2, bevelSegments: 4 }
            const geo = new THREE.ExtrudeGeometry(heartShape, extrudeSettings)
            geo.scale(0.55 * s, 0.55 * s, 0.55 * s)
            geo.center()
            return geo
        }
        case 'spiral': {
            const geometries = []
            const turns = 4, pointsPerTurn = 30
            const total = turns * pointsPerTurn
            const radius = 1.5 * s, rise = 0.12 * s, dotSize = 0.12 * s
            for (let i = 0; i < total; i++) {
                const t = (i / total) * Math.PI * 2 * turns
                const y = (i / total - 0.5) * turns * rise * pointsPerTurn
                const sphere1 = new THREE.SphereGeometry(dotSize, 6, 6)
                sphere1.translate(Math.cos(t) * radius, y, Math.sin(t) * radius)
                geometries.push(sphere1)
                const sphere2 = new THREE.SphereGeometry(dotSize, 6, 6)
                sphere2.translate(Math.cos(t + Math.PI) * radius, y, Math.sin(t + Math.PI) * radius)
                geometries.push(sphere2)
            }
            const merged = mergeGeometries(geometries)
            merged.center()
            return merged
        }
        case 'icosahedron':
            return new THREE.IcosahedronGeometry(3.0 * s, 1)
        case 'octahedron':
            return new THREE.OctahedronGeometry(3.0 * s, 1)
        case 'text':
        default:
            return createTextGeometry(params.shapeText || 'SMOKE')
    }
}

const plyCache = new Map()

export async function parsePLY(url, onProgress) {
    if (plyCache.has(url)) return plyCache.get(url)

    const response = await fetch(url)
    const contentLength = parseInt(response.headers.get('content-length') || '0')
    let buffer

    if (contentLength && onProgress && response.body) {
        const reader = response.body.getReader()
        const chunks = []
        let received = 0
        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            chunks.push(value)
            received += value.length
            onProgress(received, contentLength)
        }
        const merged = new Uint8Array(received)
        let offset = 0
        for (const chunk of chunks) {
            merged.set(chunk, offset)
            offset += chunk.length
        }
        buffer = merged.buffer
    } else {
        buffer = await response.arrayBuffer()
    }
    const bytes = new Uint8Array(buffer)

    let headerEnd = 0
    let lineStart = 0
    const decoder = new TextDecoder()
    for (let i = 0; i < Math.min(bytes.length, 8192); i++) {
        if (bytes[i] === 0x0A) {
            const line = decoder.decode(bytes.slice(lineStart, i)).trim()
            if (line === 'end_header') {
                headerEnd = i + 1
                break
            }
            lineStart = i + 1
        }
    }

    const headerText = decoder.decode(bytes.slice(0, headerEnd))
    const lines = headerText.split('\n').map(l => l.trim())

    let vertexCount = 0
    const properties = []
    let inVertex = false

    for (const line of lines) {
        if (line.startsWith('element vertex')) {
            vertexCount = parseInt(line.split(' ')[2])
            inVertex = true
        } else if (line.startsWith('element ') && inVertex) {
            inVertex = false
        } else if (line.startsWith('property') && inVertex) {
            const parts = line.split(' ')
            properties.push({ type: parts[1], name: parts[2] })
        }
    }

    const stride = properties.length * 4
    const dataView = new DataView(buffer, headerEnd)
    const positions = new Float32Array(vertexCount * 3)
    const colors = new Float32Array(vertexCount * 3)
    const opacities = new Float32Array(vertexCount)

    const propIndex = {}
    properties.forEach((p, i) => { propIndex[p.name] = i })

    const SH_C0 = 0.28209479177387814

    for (let i = 0; i < vertexCount; i++) {
        const offset = i * stride
        positions[i * 3 + 0] = dataView.getFloat32(offset + propIndex['x'] * 4, true)
        positions[i * 3 + 1] = dataView.getFloat32(offset + propIndex['y'] * 4, true)
        positions[i * 3 + 2] = dataView.getFloat32(offset + propIndex['z'] * 4, true)

        const dc0 = dataView.getFloat32(offset + propIndex['f_dc_0'] * 4, true)
        const dc1 = dataView.getFloat32(offset + propIndex['f_dc_1'] * 4, true)
        const dc2 = dataView.getFloat32(offset + propIndex['f_dc_2'] * 4, true)
        colors[i * 3 + 0] = Math.max(0, Math.min(1, 0.5 + SH_C0 * dc0))
        colors[i * 3 + 1] = Math.max(0, Math.min(1, 0.5 + SH_C0 * dc1))
        colors[i * 3 + 2] = Math.max(0, Math.min(1, 0.5 + SH_C0 * dc2))

        const rawOpacity = dataView.getFloat32(offset + propIndex['opacity'] * 4, true)
        opacities[i] = 1.0 / (1.0 + Math.exp(-rawOpacity))
    }

    const result = { positions, colors, opacities, vertexCount }
    plyCache.set(url, result)
    return result
}

const imageCache = new Map()

export async function parseImage(url) {
    if (imageCache.has(url)) return imageCache.get(url)

    const img = new Image()
    img.crossOrigin = 'anonymous'
    await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = url
    })

    const canvas = document.createElement('canvas')
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    const imageData = ctx.getImageData(0, 0, img.width, img.height)
    const pixels = imageData.data

    // Collect non-black pixels
    const validPixels = []
    for (let y = 0; y < img.height; y++) {
        for (let x = 0; x < img.width; x++) {
            const i = (y * img.width + x) * 4
            const r = pixels[i] / 255
            const g = pixels[i + 1] / 255
            const b = pixels[i + 2] / 255
            const a = pixels[i + 3] / 255
            const lum = r * 0.299 + g * 0.587 + b * 0.114
            if (lum > 0.03 && a > 0.5) {
                validPixels.push({ x, y, r, g, b, lum })
            }
        }
    }

    const vertexCount = validPixels.length
    const positions = new Float32Array(vertexCount * 3)
    const colors = new Float32Array(vertexCount * 3)
    const opacities = new Float32Array(vertexCount)

    const aspect = img.width / img.height
    const scaleX = aspect >= 1 ? 1 : aspect
    const scaleY = aspect >= 1 ? 1 / aspect : 1

    for (let i = 0; i < vertexCount; i++) {
        const p = validPixels[i]
        positions[i * 3 + 0] = ((p.x / img.width) - 0.5) * scaleX
        positions[i * 3 + 1] = -((p.y / img.height) - 0.5) * scaleY
        positions[i * 3 + 2] = 0
        colors[i * 3 + 0] = p.r
        colors[i * 3 + 1] = p.g
        colors[i * 3 + 2] = p.b
        opacities[i] = Math.min(1.0, p.lum * 2.0 + 0.3)
    }

    const result = { positions, colors, opacities, vertexCount }
    imageCache.set(url, result)
    return result
}
